<script setup lang="ts">
import type { DropdownMenuItem } from '@nuxt/ui';
import type { ResolvedMonospacePrimitiveField } from '#layers/data-model/public';
import { useClipboard, usePermission } from '@vueuse/core';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { isNil } from '#layers/base/public';
import { parseRawValue, serializeRawValue } from '#layers/data-model/public';
import { useFieldNullable } from '#layers/forms/composables/use-field-nullable';
import { useRevertMenuItem } from '#layers/forms/composables/use-revert-menu-item';
import { showConfirmationDialog, useCopyRawValueMenuItem } from '#layers/ui/public';
import FieldLabelEditDialog from './FieldLabelEditDialog.vue';
import FieldLabelMenu from './FieldLabelMenu.vue';

const props = defineProps<{
	label: string; // Used for aria-label, not rendered
	primitiveField?: ResolvedMonospacePrimitiveField;
	disabled?: boolean;
	dirty?: boolean;
	/** Value the form was loaded with, restored by the revert action. */
	originalValue?: unknown;
}>();

const model = defineModel<unknown>();

const nullable = useFieldNullable(() => props.primitiveField);

const { t } = useI18n();
const { copy, copied, text } = useClipboard({ read: true });
const clipboardReadPermission = usePermission('clipboard-read');
const clipboardWritePermission = usePermission('clipboard-write');

const buildCopyRawValueMenuItem = useCopyRawValueMenuItem();

const revertItem = useRevertMenuItem({
	dirty: () => props.dirty,
	disabled: () => props.disabled,
	onRevert: revertValue,
});

const menuItems = computed<DropdownMenuItem[][]>(() => {
	const rawValueActions: DropdownMenuItem[] = [
		{
			label: t('common.edit_raw_value'),
			icon: 'lucide:code',
			onSelect: openEditModal,
		},
		buildCopyRawValueMenuItem({
			copied,
			onCopy: copyValue,
			disabled: () => clipboardWritePermission.value === 'denied',
		}),
		{
			label: t('common.paste_raw_value'),
			icon: 'lucide:clipboard-paste',
			disabled: clipboardReadPermission.value === 'denied' || props.disabled,
			onSelect: pasteValue,
		},
	];

	const valueResets: DropdownMenuItem[] = [];

	if (nullable.value && !isNil(model.value)) {
		valueResets.push({
			label: t('common.clear_value'),
			icon: 'lucide:trash-2',
			onSelect: clearValue,
		});
	}

	if (revertItem.value) {
		valueResets.push(revertItem.value);
	}

	// An empty group would render a stray separator.
	return [rawValueActions, valueResets].filter((group) => group.length > 0);
});

function copyValue() {
	copy(props.primitiveField?.type ? serializeRawValue(model.value, props.primitiveField.type) : String(model.value));
}

function revertValue() {
	model.value = props.originalValue;
}

function pasteValue() {
	model.value = props.primitiveField?.type ? parseRawValue(text.value, props.primitiveField.type) : text.value;
}

async function openEditModal() {
	const result = await showConfirmationDialog({
		component: FieldLabelEditDialog,
		props: {
			value: model.value,
			primitiveField: props.primitiveField,
			disabled: props.disabled,
		},
	});

	if (result) {
		model.value = result.value;
	}
}

function clearValue() {
	model.value = null;
}
</script>

<template>
	<FieldLabelMenu :label :items="menuItems" />
</template>
